import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { setAuthSessionCookies } from "@/lib/authCookies";
import {
  mapTrialSignupError,
  registerUniversalTrialAccount,
  trialSignupFailureGuide,
} from "@/lib/landingSignup";
import { trackRegistrationIfAuthenticated } from "@/lib/leadEvents";
import { canSubmitUniversalSignup, useSignupNiches } from "@/lib/signupNiches";
import SignupNicheSelect from "@/components/SignupNicheSelect";
import { validateEmail } from "./utils";
import {
  SIGNUP_TITLE,
  SIGNUP_SUBTITLE,
  SIGNUP_SUBMIT,
  SIGNUP_MICRO,
  SIGNUP_SUCCESS_TITLE,
  SIGNUP_SUCCESS_BODY,
  SIGNUP_OPEN_DASHBOARD,
  SIGNUP_EXISTING_EMAIL,
} from "./signupContent";

const ROOFING_NICHE_ID = "roofing_company";

const SignupModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitLocked, setSubmitLocked] = useState(false);
  const [recoveryLinks, setRecoveryLinks] = useState([]);
  const [isSuccess, setIsSuccess] = useState(false);
  const submitting = useRef(false);
  const nicheCatalog = useSignupNiches();
  const roofingChoice = nicheCatalog.niches.find((row) => row.niche_id === ROOFING_NICHE_ID) || null;
  const roofingReady = canSubmitUniversalSignup({
    catalogStatus: nicheCatalog.status,
    nicheId: ROOFING_NICHE_ID,
    niches: nicheCatalog.niches,
  });

  if (!isOpen) return null;

  const resetState = () => {
    setEmail("");
    setError("");
    setTouched(false);
    setIsLoading(false);
    setIsSuccess(false);
    setSubmitLocked(false);
    setRecoveryLinks([]);
  };

  const handleClose = () => {
    if (isLoading) return;
    resetState();
    onClose();
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    if (touched && value) {
      if (validateEmail(value)) {
        setError("");
      } else {
        setError("Please enter a valid email address");
      }
    } else if (touched && !value) {
      setError("Email is required");
    }
  };

  const handleBlur = () => {
    setTouched(true);
    if (!email) {
      setError("Email is required");
    } else if (!validateEmail(email)) {
      setError("Please enter a valid email address");
    } else {
      setError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading || submitLocked || submitting.current) return;
    setTouched(true);

    if (!email) {
      setError("Email is required");
      return;
    }
    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }
    if (!roofingReady) return;

    setIsLoading(true);
    setError("");
    setRecoveryLinks([]);
    submitting.current = true;

    try {
      const data = await registerUniversalTrialAccount({
        email,
        nicheId: ROOFING_NICHE_ID,
      });

      if (data.status === "ok") {
        const token = typeof data.token === "string" ? data.token.trim() : "";
        if (!token || data.bot_type !== "universal") {
          setError("Unexpected response from the server. Please try again.");
          return;
        }

        const normalizedEmail = email.trim().toLowerCase();
        setAuthSessionCookies({
          token,
          email: normalizedEmail,
          accountType: data.account_type || "client",
          botType: "universal",
        });

        trackRegistrationIfAuthenticated().catch((err) =>
          console.warn("[leadEvents] is_registered failed", err),
        );

        setIsSuccess(true);
        return;
      }

      if (data.status === "error") {
        if (data.message === "The user already exists") {
          setError(SIGNUP_EXISTING_EMAIL);
        } else {
          setError(data.message || "An error occurred. Please try again.");
        }
        return;
      }

      setError("Unexpected response from the server. Please try again.");
    } catch (err) {
      console.error("Roofing trial registration error:", err);
      const guide = trialSignupFailureGuide(err);
      setError(mapTrialSignupError(err, SIGNUP_EXISTING_EMAIL));
      const holdSubmit = guide.code === "ACCOUNT_SETUP_INCOMPLETE" || (!err?.response && Boolean(err?.request));
      setRecoveryLinks(holdSubmit ? guide.links : []);
      setSubmitLocked(holdSubmit);
    } finally {
      submitting.current = false;
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      handleClose();
    }
  };

  const isValid = email && validateEmail(email);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4 py-6 overflow-y-auto"
      onClick={handleBackdropClick}
      data-testid="signup-modal-overlay"
    >
      <div
        className="relative w-full max-w-md glass rounded-2xl p-8 border border-orange-500/20 animate-fade-in-up my-auto"
        data-testid="signup-modal"
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors disabled:opacity-50"
          data-testid="signup-close-button"
          aria-label="Close popup"
          disabled={isLoading}
        >
          <X className="w-5 h-5" />
        </button>

        {!isSuccess ? (
          <div data-testid="signup-modal-form">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-[#F3F4F6] text-center mb-2 pr-6">
              {SIGNUP_TITLE}
            </h2>
            <p
              className="font-body text-sm text-[#9CA3AF] text-center leading-relaxed mb-6 pr-6"
              data-testid="signup-modal-subtitle"
            >
              {SIGNUP_SUBTITLE}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div data-testid="signup-preselected-niche">
                <SignupNicheSelect
                  niches={roofingChoice ? [roofingChoice] : []}
                  value={ROOFING_NICHE_ID}
                  onChange={() => {}}
                  family="universal"
                  catalogStatus={nicheCatalog.status}
                  errorMessage={nicheCatalog.errorMessage}
                  onRetry={nicheCatalog.retry}
                  selectId="signup-roofing-niche"
                  disabled={isLoading || submitLocked}
                />
                <p data-testid="signup-preselected-niche-id">{ROOFING_NICHE_ID}</p>
              </div>
              <div>
                <label htmlFor="signup-email-input" className="block text-[#9CA3AF] text-sm mb-2 font-body">
                  Email
                </label>
                <input
                  id="signup-email-input"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleBlur}
                  placeholder="your@email.com"
                  disabled={isLoading}
                  className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body disabled:opacity-50 ${
                    error && touched
                      ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
                      : isValid
                      ? "border-green-500/50 focus:border-green-500 focus:ring-1 focus:ring-green-500/50"
                      : "border-white/10 focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50"
                  }`}
                  data-testid="signup-email-input"
                />
                {error && touched && (
                  <p className="mt-2 text-red-400 text-xs font-body" data-testid="signup-email-error">
                    {error}
                  </p>
                )}
                {recoveryLinks.length > 0 && (
                  <p className="mt-2 text-xs font-body" data-testid="signup-recovery">
                    <Link to={recoveryLinks[0]}>Log in</Link>
                    {" "}
                    <Link to={recoveryLinks[1]}>Reset password</Link>
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={!isValid || !roofingReady || isLoading || submitLocked}
                className={`w-full font-heading font-bold text-base md:text-lg py-4 px-6 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 ${
                  isValid && roofingReady && !isLoading && !submitLocked
                    ? "btn-pulse-fiery bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-400 hover:to-red-500"
                    : "bg-orange-500/50 text-white/70 cursor-not-allowed"
                }`}
                data-testid="signup-submit"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Creating your account...
                  </>
                ) : (
                  <>
                    {SIGNUP_SUBMIT}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              <p className="text-[#9CA3AF]/70 text-xs text-center pt-2">{SIGNUP_MICRO}</p>
            </form>
          </div>
        ) : (
          <div data-testid="signup-modal-success">
            <div className="flex items-center justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-green-500/15 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-400" />
              </div>
            </div>
            <h2 className="font-heading font-bold text-xl md:text-2xl text-[#F3F4F6] text-center mb-4 pr-6">
              {SIGNUP_SUCCESS_TITLE}
            </h2>
            <p className="font-body text-sm md:text-[15px] text-[#D1D5DB] text-center leading-relaxed">
              {SIGNUP_SUCCESS_BODY()}
            </p>

            <Link
              to="/dashboard"
              className="mt-6 w-full font-heading font-bold text-base md:text-lg py-4 px-6 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 btn-pulse-fiery bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-400 hover:to-red-500"
              data-testid="signup-open-dashboard"
              onClick={() => {
                resetState();
                onClose();
              }}
            >
              {SIGNUP_OPEN_DASHBOARD}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignupModal;
